/**
 * mergePolygonWithStrokes returns { rings: [...] } in image space.
 * One ring → store as [[x,y],...]; several (e.g. brush blob separate from mask) → [[[x,y],...], ...].
 */
export function polygonFromMergeResult(merged) {
  const rings = merged?.rings;
  if (!Array.isArray(rings) || rings.length === 0) return null;
  if (rings.length === 1) return rings[0];
  return rings;
}

/** Normalize polygon to array of rings [[[x,y],...], ...] - handles all API/taskResult formats */
export function normalizePolygonToRings(poly) {
  if (!poly) return [[]];
  if (poly.coordinates && Array.isArray(poly.coordinates)) {
    return poly.coordinates;
  }
  if (poly.points && Array.isArray(poly.points)) {
    return [poly.points, ...(poly.holes ?? [])];
  }
  if (!Array.isArray(poly)) return [[]];
  if (poly.length === 0) return [[]];
  const first = poly[0];
  if (Array.isArray(first) && first.length >= 2) {
    const isPoint = typeof first[0] === 'number' && typeof first[1] === 'number';
    if (isPoint) {
      return [poly];
    }
    if (Array.isArray(first[0])) {
      return poly;
    }
    const flat = poly.flat(2);
    if (flat.length >= 4) {
      const points = [];
      for (let i = 0; i < flat.length - 1; i += 2) {
        points.push([Number(flat[i]), Number(flat[i + 1])]);
      }
      return [points];
    }
    return [poly];
  }
  const flat = poly.flat(2);
  if (flat.length < 4) return [[]];
  const points = [];
  for (let i = 0; i < flat.length - 1; i += 2) {
    points.push([Number(flat[i]), Number(flat[i + 1])]);
  }
  return [points];
}

/** Convert polygon item to API interiors format { interior_type_id?, confidence, bbox, polygon } - all coords as integers */
export function itemToInteriorFormat(item) {
  const poly = item.polygon ?? item.polygons;
  const rawRings = normalizePolygonToRings(poly);
  const polygon = rawRings
    .map((ring) =>
      (ring ?? [])
        .map((pt) => {
          const x = Array.isArray(pt) ? pt[0] : pt?.x ?? pt?.lng ?? 0;
          const y = Array.isArray(pt) ? pt[1] : pt?.y ?? pt?.lat ?? 0;
          const xi = Math.round(Number(x));
          const yi = Math.round(Number(y));
          return [Number.isNaN(xi) ? 0 : xi, Number.isNaN(yi) ? 0 : yi];
        })
    )
    .filter((ring) => ring.length >= 3);
  let bbox = (item.bbox ?? []).map((v) => {
    const n = Math.round(Number(v));
    return Number.isNaN(n) ? 0 : n;
  });
  if (bbox.length === 0 && polygon.length > 0 && polygon[0]?.length >= 3) {
    const pts = polygon[0];
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (const [px, py] of pts) {
      if (px < minX) minX = px;
      if (py < minY) minY = py;
      if (px > maxX) maxX = px;
      if (py > maxY) maxY = py;
    }
    bbox = Number.isFinite(minX) ? [minX, minY, maxX, maxY] : [0, 0, 0, 0];
  } else if (bbox.length === 0) {
    bbox = [0, 0, 0, 0];
  }
  let confidence = Number(item.confidence ?? 0);
  if (confidence >= 1 || Number.isNaN(confidence)) confidence = 0.9999;
  if (confidence < 0) confidence = 0;
  const base = {
    confidence,
    bbox,
    polygon: polygon.length > 0 ? polygon : [[[0, 0], [1, 0], [0, 1]]],
  };
  const interiorTypeId = item.interior_type_id ?? item.interiorTypeId;
  return interiorTypeId != null ? { ...base, interior_type_id: interiorTypeId } : base;
}
